import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { ImageUploader } from '../../components/admin/ImageUploader';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Pencil, Trash2, Loader2, Save, ListOrdered, Image as ImageIcon, X } from 'lucide-react';

const inputCls = 'mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/35';
const empty = { name: '', short_description: '', description: '', image_url: '', gallery: [], duration: '', price: '', compare_at_price: '', includes: [], visa_policy_residence: '', visa_policy_tourist: '', instagram_url: '', whatsapp_url: '', book_url: '', category: 'oman_tours', active: true, order: 0 };
const CATEGORY_LABELS = { oman_tours: 'Oman Tours', diving_trips: 'Diving Trips' };

export default function TripsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get('/trips?only_active=false');
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing({ ...empty, includes_text: '', gallery_text: '' }); setOpen(true); };
  const startEdit = (it) => { setEditing({ ...it, includes_text: (it.includes || []).join('\n'), gallery_text: (it.gallery || []).join('\n') }); setOpen(true); };

  const addGalleryImage = (url) => {
    if (!url) return;
    setEditing((p) => ({ ...p, gallery_text: (p.gallery_text || '') + (p.gallery_text ? '\n' : '') + url }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      payload.includes = (payload.includes_text || '').split('\n').map((x) => x.trim()).filter(Boolean);
      payload.gallery = (payload.gallery_text || '').split('\n').map((x) => x.trim()).filter(Boolean);
      delete payload.includes_text; delete payload.gallery_text;
      delete payload.created_at; delete payload.updated_at;
      if (editing.id) {
        await api.patch(`/trips/${editing.id}`, payload);
        toast.success('Trip updated');
      } else {
        await api.post('/trips', payload);
        toast.success('Trip created');
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`Delete trip “${it.name}”?`)) return;
    try { await api.delete(`/trips/${it.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="font-display text-xl font-semibold">Tours & Trips</div>
          <div className="text-xs text-white/55">Manage Oman Tours and Diving Trips — pick the category per item</div>
        </div>
        <Button onClick={startCreate} data-testid="admin-add-trip-button" className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] rounded-xl"><Plus className="mr-1 h-4 w-4" /> Add trip</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && <div className="col-span-full rounded-3xl border border-white/10 bg-white/5 p-10 text-center text-white/55">No trips yet. Click “Add trip” to create your first one.</div>}
        {items.map((it) => (
          <Card key={it.id} className="rounded-2xl border-white/10 bg-white/5 overflow-hidden">
            <div className="aspect-[16/9] bg-[#0d223d] relative">
              {it.image_url ? <img src={it.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
              {Array.isArray(it.gallery) && it.gallery.length > 0 && (
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[#0a1628]/85 border border-white/15 text-white/75"><ImageIcon className="h-3 w-3" /> {it.gallery.length} photos</div>
              )}
              {!it.active && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#0a1628]/85 border border-white/15 text-white/75">Hidden</span>}
              <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#00b4d8]/20 border border-[#00b4d8]/40 text-[#00b4d8] font-semibold">
                {CATEGORY_LABELS[it.category || 'oman_tours']}
              </span>
            </div>
            <div className="p-4">
              <div className="font-display font-semibold">{it.name}</div>
              <div className="text-xs text-white/55 mt-1 line-clamp-2">{it.short_description}</div>
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid="admin-trip-edit" className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(it)} data-testid="admin-trip-delete" className="text-red-400 hover:text-red-200 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                <div className="ml-auto text-[11px] text-white/45 inline-flex items-center gap-1"><ListOrdered className="h-3 w-3" /> {it.order}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-[#07101f] border-white/12 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing?.id ? 'Edit trip' : 'New trip'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[72vh] overflow-y-auto scrollbar-thin pr-1">
              <div>
                <Label>Name</Label>
                <Input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={editing.category || 'oman_tours'}
                  onValueChange={(v) => setEditing({ ...editing, category: v })}
                >
                  <SelectTrigger data-testid="admin-trip-category" className={`${inputCls} w-full`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#07101f] border-white/15 text-white">
                    <SelectItem value="oman_tours" className="focus:bg-white/10 focus:text-white">Oman Tours — appears under “Oman Tours” menu</SelectItem>
                    <SelectItem value="diving_trips" className="focus:bg-white/10 focus:text-white">Diving Trips — appears under “Diving Trips” menu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ImageUploader label="Main image (card cover)" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Duration</Label><Input className={inputCls} value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} /></div>
                <div><Label>Price (after discount)</Label><Input className={inputCls} placeholder="AED 500" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
              </div>
              <div>
                <Label>Original price <span className="text-white/45 text-xs">(optional — shown crossed-out with a % OFF badge)</span></Label>
                <Input className={inputCls} placeholder="AED 700" value={editing.compare_at_price || ''} onChange={(e) => setEditing({ ...editing, compare_at_price: e.target.value })} />
              </div>
              <div>
                <Label>Short description (card) <span className="text-white/45 text-xs">(up to 500 chars — collapsed on cards with a Read more toggle)</span></Label>
                <Textarea rows={4} maxLength={500} className={inputCls} value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} />
                <div className={`text-[11px] mt-1 text-right ${((editing.short_description || '').length) >= 500 ? 'text-red-300' : 'text-white/40'}`}>
                  {(editing.short_description || '').length} / 500 chars
                </div>
              </div>
              <div>
                <Label>Full description (detail page) — supports paragraphs</Label>
                <Textarea rows={6} className={inputCls} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div>
                <Label>Photo gallery (one URL per line)</Label>
                <Textarea rows={5} className={inputCls} placeholder="/trips/khasab/photo3.jpg…" value={editing.gallery_text} onChange={(e) => setEditing({ ...editing, gallery_text: e.target.value })} />
                <div className="mt-2">
                  <ImageUploader label="Upload a new photo to add to gallery" value="" onChange={addGalleryImage} />
                </div>
              </div>
              <div>
                <Label>Includes (one per line)</Label>
                <Textarea rows={5} className={inputCls} value={editing.includes_text} onChange={(e) => setEditing({ ...editing, includes_text: e.target.value })} />
              </div>
              <div>
                <Label>Visa policy — UAE Residence</Label>
                <Textarea rows={5} className={inputCls} value={editing.visa_policy_residence} onChange={(e) => setEditing({ ...editing, visa_policy_residence: e.target.value })} />
              </div>
              <div>
                <Label>Visa policy — UAE Tourist</Label>
                <Textarea rows={5} className={inputCls} value={editing.visa_policy_tourist} onChange={(e) => setEditing({ ...editing, visa_policy_tourist: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Instagram URL (optional, per-trip)</Label><Input className={inputCls} placeholder="https://instagram.com/..." value={editing.instagram_url} onChange={(e) => setEditing({ ...editing, instagram_url: e.target.value })} /></div>
                <div><Label>WhatsApp URL (optional, per-trip)</Label><Input className={inputCls} placeholder="https://wa.me/9715..." value={editing.whatsapp_url} onChange={(e) => setEditing({ ...editing, whatsapp_url: e.target.value })} /></div>
              </div>
              <div>
                <Label>External Book Now URL (optional)</Label>
                <Input className={inputCls} placeholder="https:// or mailto: or wa.me/..." value={editing.book_url} onChange={(e) => setEditing({ ...editing, book_url: e.target.value })} />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                  <Label>Visible on site</Label>
                </div>
                <div className="flex items-center gap-2"><Label>Order</Label><Input type="number" className={`w-20 ${inputCls}`} value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || '0') })} /></div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/65 hover:text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={save} disabled={saving || !editing?.name} className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100]">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving</> : <><Save className="mr-2 h-4 w-4" /> Save</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
